import {
  type TimeInputProps as BaseTimeInputProps,
  TimeInput as BaseTimeInput,
} from '@mantine/dates';
import { useMergedRef } from '@mantine/hooks';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import { alpha, styled } from '@mui/material/styles';
import { forwardRef, useEffect, useRef, useState } from 'react';

import 'dayjs/locale/th';

import { AccessTimeIcon, CloseRoundedIcon } from '../../icons';
import { ColorPalette } from '../../theme';

type TimeInputProps = Omit<BaseTimeInputProps, 'sx' | 'onChange'> & {
  helperText?: string;
  fullWidth?: boolean;
  onChange?: (value: Date | null) => void;
};

const StyledInputLabel = styled(InputLabel)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const StyledTimeInput = styled(BaseTimeInput, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'error',
})<Omit<TimeInputProps, 'sx'> & { color?: ColorPalette; error?: boolean }>(({
  theme,
  color: colorProp,
  error,
}) => {
  const color = (error ? ColorPalette.ERROR : colorProp) ?? ColorPalette.PRIMARY;
  return {
    marginTop: theme.spacing(6),
    border: '1px solid',
    borderColor: error ? theme.palette.error.main : theme.palette.grey['300'],
    borderRadius: theme.shape.rounded(1),
    'input, .mantine-Text-root': {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 400,
    },
    'input::placeholder, .mantine-Text-root': {
      color: theme.palette.text.disabled,
      fontSize: 18,
      textAlign: 'center',
    },
    ':has(input[value=""]) .mantine-Text-root': {
      color: `${theme.palette.text.disabled} !important`,
    },
    '.mantine-TimeInput-input': {
      paddingLeft: 10,
      border: 'none',
      minHeight: '42px',
      boxSizing: 'border-box',
      height: '1.4375em',
      ':hover': { borderColor: theme.palette[color].main },
      '&.mantine-TimeInput-disabled': {
        backgroundColor: '#F1F3F5',
        ':hover': { borderColor: theme.palette.grey['300'] },
        '&,input:disabled': {
          cursor: 'default',
          opacity: 1,
          color: theme.palette.text.disabled,
        },
      },
      ':has(input:focus)': {
        borderColor: theme.palette[color].main,
        boxShadow: `${alpha(theme.palette[color].main, 0.25)} 0 0 0 3px`,
      },
    },
  };
});

const TimeInput = forwardRef((props: TimeInputProps, forwardedRef) => {
  const {
    label,
    value,
    onChange,
    helperText,
    disabled: isDisabled,
    error,
    color,
    clearable: isClearable = true,
    fullWidth: isFullWidth = true,
    required: isRequired,
    ...restProps
  } = props;

  const timeInputRef = useRef<HTMLInputElement>(null);
  const ref = useMergedRef(timeInputRef, forwardedRef);
  const [time, setTime] = useState<Date | null>(value ? new Date(value) : null);

  useEffect(() => {
    setTime(value ? new Date(value) : null);
  }, [value]);

  const handleTimeChange = (value: Date | null) => {
    setTime(value);
    onChange?.(value);
  };

  const handleClear = () => {
    setTime(null);
    onChange?.(null);
  };

  const rightButton =
    !isClearable || !time ? (
      <AccessTimeIcon
        sx={{
          color: !isDisabled && time ? 'text.secondary' : 'text.disabled',
          pointerEvents: 'none',
          fontSize: 24,
        }}
      />
    ) : (
      <IconButton onClick={handleClear} disabled={props.disabled} sx={{ color: 'text.secondary' }}>
        <CloseRoundedIcon />
      </IconButton>
    );

  return (
    <FormControl fullWidth={isFullWidth} required={isRequired}>
      {label ? <StyledInputLabel>{label}</StyledInputLabel> : null}
      <StyledTimeInput
        ref={ref}
        value={time}
        color={color as ColorPalette}
        size='md'
        rightSection={rightButton}
        rightSectionProps={{ style: { width: 0, right: 24 } }}
        onChange={handleTimeChange}
        error={!!error}
        disabled={isDisabled}
        {...restProps}
      />
      <FormHelperText error={!!error}>{helperText}</FormHelperText>
    </FormControl>
  );
});

TimeInput.displayName = 'TimeInput';

export { TimeInput };
export type { TimeInputProps };
